import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import * as apiActions from 'api-actions'
import * as LS from 'local-storage'
import {
  SESSION_EXPIRY_CHECK_INTERVAL_MS,
  SESSION_EXPIRY_WARNING_SECONDS,
} from 'config'
import { getSecondsUntilJwtExpiry } from 'utils/local/jwt-expiry'

function readPortalToken() {
  return (
    LS.getSchoolAuthToken() ||
    LS.getPartnerAuthToken() ||
    LS.getArtistAuthToken()
  )
}

function SessionExpiryMonitor({ refreshSession, redirectToSignOut }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [extending, setExtending] = useState(false)
  const [refreshError, setRefreshError] = useState(null)
  const returnFocusRef = useRef(null)
  const continueRef = useRef(null)

  const closeDialogReturnFocus = useCallback(() => {
    setDialogOpen(false)
    setRefreshError(null)
    const el = returnFocusRef.current
    if (el && typeof el.focus === 'function') {
      try {
        el.focus()
      } catch {
        /* ignore */
      }
    }
  }, [])

  const handleContinue = useCallback(() => {
    setExtending(true)
    setRefreshError(null)
    refreshSession()
      .then((data) => {
        if (data && data.token) {
          LS.replaceActivePortalAuthToken(data.token)
          closeDialogReturnFocus()
        } else {
          setRefreshError(
            'Could not refresh your session. Try again or sign out.'
          )
        }
      })
      .catch(() => {
        setRefreshError(
          'Could not refresh your session. Try again or sign out.'
        )
      })
      .finally(() => setExtending(false))
  }, [refreshSession, closeDialogReturnFocus])

  useEffect(() => {
    const check = () => {
      const token = readPortalToken()
      if (!token) return
      const left = getSecondsUntilJwtExpiry(token)
      if (left == null) return
      if (left <= SESSION_EXPIRY_WARNING_SECONDS && left > 0) {
        setDialogOpen(true)
      }
    }
    check()
    const id = setInterval(check, SESSION_EXPIRY_CHECK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!dialogOpen) return
    returnFocusRef.current = document.activeElement
    const t = window.setTimeout(() => {
      if (continueRef.current) continueRef.current.focus()
    }, 50)
    return () => clearTimeout(t)
  }, [dialogOpen])

  useEffect(() => {
    if (!dialogOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (continueRef.current) continueRef.current.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dialogOpen])

  useEffect(() => {
    if (!dialogOpen) return
    const tick = () => {
      const token = readPortalToken()
      const left = token ? getSecondsUntilJwtExpiry(token) : null
      setSecondsLeft(left != null && left > 0 ? left : 0)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [dialogOpen])

  if (!dialogOpen) return null

  const secondsMessage =
    secondsLeft != null && secondsLeft > 0
      ? `Time remaining before sign-out: ${secondsLeft} seconds.`
      : 'Your session is ending. Choose Continue to stay signed in.'

  return (
    <div className="session-expiry-overlay" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expiry-title"
        className="session-expiry-dialog"
      >
        <h2 id="session-expiry-title" className="session-expiry-title">
          Continue your session?
        </h2>
        <p className="session-expiry-body">
          For security you will be signed out when this session ends. Press{' '}
          <strong>Continue session</strong> (or activate the same control with
          Space or Enter) to stay signed in.
        </p>
        <p className="session-expiry-count" aria-live="polite">
          {secondsMessage}
        </p>
        {refreshError && (
          <p className="session-expiry-error" role="alert">
            {refreshError}
          </p>
        )}
        <div className="session-expiry-actions">
          <button
            ref={continueRef}
            type="button"
            className="button-primary"
            disabled={extending}
            onClick={handleContinue}
          >
            {extending ? 'Continuing…' : 'Continue session'}
          </button>
          <button
            type="button"
            className="button-secondary session-expiry-sign-out"
            disabled={extending}
            onClick={() => redirectToSignOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

SessionExpiryMonitor.propTypes = {
  refreshSession: PropTypes.func.isRequired,
  redirectToSignOut: PropTypes.func.isRequired,
}

export default connect(null, {
  refreshSession: apiActions.refreshSession,
})(SessionExpiryMonitor)
